const isEmptyValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  
  if (typeof value === "string") {
    return value.trim().length === 0;
  }
  
  if (typeof value === "number") {
    return value <= 0;
  }
  
  if (typeof value === "boolean") {
    return value === false;
  }
  
  if (value instanceof Date) return false;
  
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}/)) return false;
  
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  
  if (typeof value === "object" && !Array.isArray(value)) {
    return Object.keys(value).length === 0;
  }
  
  return true;
};

export const hasAnyData = (item: any, excludeFields: string[] = []): boolean => {
  if (!item || typeof item !== 'object') return false;
  
  return Object.keys(item).some(key => {
    if (excludeFields.includes(key)) return false;
    return !isEmptyValue(item[key]);
  });
};

export interface DefaultValuesConfig {
  flights?: {
    airline?: string;
    flight_number?: string;
    departure_airport?: string;
    arrival_airport?: string;
    departure_datetime?: string | (() => string);
    arrival_datetime?: string | (() => string);
  };
  airport_services?: {
    service_date?: string | (() => string);
  };
  transfers?: {
    transfer_type?: string;
    transfer_date?: string | (() => string);
  };
  accommodations?: {
    number_of_rooms?: number;
    check_in?: string | (() => string);
    check_out?: string | (() => string);
  };
  tours?: {
    tour_date?: string | (() => string);
  };
  attractions?: {
    attraction_date?: string | (() => string);
    ticket_quantity?: number;
  };
  additional_requests?: {
    request_title?: string;
    request_price?: number;
  };
  insurance?: {
    insurance_title?: string;
    insurance_price?: number;
  };
}

export const applyDefaultValues = (
  tableName: string,
  itemData: Record<string, any>
): Record<string, any> => {
  const cleaned = { ...itemData };

  switch (tableName) {
    case "proposal_flights":
      if (!cleaned.airline || cleaned.airline.trim() === "") {
        cleaned.airline = "TBD";
      }
      if (!cleaned.flight_number || cleaned.flight_number.trim() === "") {
        cleaned.flight_number = "TBD";
      }
      if (!cleaned.departure_airport || cleaned.departure_airport.trim() === "") {
        cleaned.departure_airport = "TBD";
      }
      if (!cleaned.arrival_airport || cleaned.arrival_airport.trim() === "") {
        cleaned.arrival_airport = "TBD";
      }
      if (!cleaned.departure_datetime) {
        cleaned.departure_datetime = new Date().toISOString();
      }
      if (!cleaned.arrival_datetime) {
        cleaned.arrival_datetime = new Date().toISOString();
      }
      break;
      
    case "proposal_airport_services":
      if (!cleaned.service_date) {
        cleaned.service_date = new Date().toISOString().split('T')[0];
      }
      break;
      
    case "proposal_transfers":
      if (!cleaned.transfer_type || cleaned.transfer_type.trim() === "") {
        cleaned.transfer_type = "TBD";
      }
      if (!cleaned.transfer_date) {
        cleaned.transfer_date = new Date().toISOString().split('T')[0];
      }
      break;
      
    case "proposal_accommodations":
      if (!cleaned.number_of_rooms) {
        cleaned.number_of_rooms = 1;
      }
      if (!cleaned.check_in) {
        cleaned.check_in = new Date().toISOString().split('T')[0];
      }
      if (!cleaned.check_out) {
        const checkOutDate = new Date();
        checkOutDate.setDate(checkOutDate.getDate() + 1);
        cleaned.check_out = checkOutDate.toISOString().split('T')[0];
      }
      break;
      
    case "proposal_tours":
      if (!cleaned.tour_date) {
        cleaned.tour_date = new Date().toISOString().split('T')[0];
      }
      break;
      
    case "proposal_attractions":
      if (!cleaned.attraction_date) {
        cleaned.attraction_date = new Date().toISOString().split('T')[0];
      }
      if (!cleaned.ticket_quantity) {
        cleaned.ticket_quantity = 1;
      }
      break;
      
    case "proposal_additional_requests":
      if (!cleaned.request_title || cleaned.request_title.trim() === "") {
        cleaned.request_title = "TBD";
      }
      if (!cleaned.request_price) {
        cleaned.request_price = 0;
      }
      break;
      
    case "proposal_insurance":
      if (!cleaned.insurance_title || cleaned.insurance_title.trim() === "") {
        cleaned.insurance_title = "TBD";
      }
      if (!cleaned.insurance_price) {
        cleaned.insurance_price = 0;
      }
      break;
  }
  
  return cleaned;
};

export const sanitizeItem = (
  item: Record<string, any>,
  excludeFields: string[] = []
): Record<string, any> => {
  const cleaned: Record<string, any> = {};
  
  Object.keys(item).forEach(key => {
    if (excludeFields.includes(key)) return;
    
    const value = item[key];
    if (value !== "" && value !== null && value !== undefined) {
      cleaned[key] = value;
    }
  });
  
  return cleaned;
};

export const validateAndSanitizeItems = (
  items: any[] | undefined,
  proposalId: string,
  excludeFields: string[] = [],
  relationKeys: string[] = [],
  customValidator?: (item: any) => boolean
): Record<string, any>[] => {
  const allItems = items || [];
  
  const standardExcludeFields = ["id", "created_at", "updated_at", "proposal_id", ...excludeFields, ...relationKeys];
  
  const filteredItems = customValidator
    ? allItems.filter(item => !customValidator(item))
    : allItems.filter(item => hasAnyData(item, standardExcludeFields));
  
  return filteredItems.map(item => {
    const cleaned = sanitizeItem(item, ["created_at", "updated_at", ...relationKeys]);
    cleaned.proposal_id = proposalId;
    return cleaned;
  });
};

